import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { Shell } from "@/components/Shell";
import { StatusBadge } from "@/components/StatusBadge";
import { InvoiceDialog } from "@/components/InvoiceDialog";
import { money, fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LayoutDashboard, Users, UtensilsCrossed, Boxes, ReceiptText, Settings as SettingsIcon, ScrollText, Plus, Pencil, Trash2, Loader2, TrendingUp, ShoppingCart, Wallet, AlertTriangle, Receipt, BarChart3, CalendarClock, Upload, KeyRound, Eye, EyeOff, Tags, Coins } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import { ExpensesPanel } from "@/components/ExpensesPanel";
import { ReportsPanel } from "@/components/ReportsPanel";
import { ClubOpsPanel } from "@/components/ClubOpsPanel";
import { StockAdjustDialog } from "@/components/StockAdjustDialog";
import { OrderEditDialog } from "@/components/OrderEditDialog";
import { resolveUrl } from "@/lib/format";
import { toast } from "sonner";

export default function AdminDashboard() {
  const { settings, reload } = useSettings();
  const sym = settings.currency_symbol || "\u20b9";
  const [tab, setTab] = useState("overview");
  const [summary, setSummary] = useState(null);
  const [users, setUsers] = useState([]);
  const [menu, setMenu] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [orders, setOrders] = useState([]);
  const [editOrder, setEditOrder] = useState(null);
  const [audit, setAudit] = useState([]);
  const [groups, setGroups] = useState([]);
  const [chargeTypes, setChargeTypes] = useState([]);
  const [groupDlg, setGroupDlg] = useState(null);
  const [groupForm, setGroupForm] = useState({});
  const [chargeDlg, setChargeDlg] = useState(null);
  const [chargeForm, setChargeForm] = useState({});
  const [userGroupFilter, setUserGroupFilter] = useState("all");
  const [payFor, setPayFor] = useState(null);
  const [payAmt, setPayAmt] = useState("");
  const [payMode, setPayMode] = useState("cash");
  const [payRemarks, setPayRemarks] = useState("");
  const [loading, setLoading] = useState(true);
  const [viewInv, setViewInv] = useState(null);
  const [adjItem, setAdjItem] = useState(null);
  const [resetDlg, setResetDlg] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);
  const [correctInv, setCorrectInv] = useState(null);
  const [correctItems, setCorrectItems] = useState([]);
  const [correctReason, setCorrectReason] = useState("");
  const setCI = (i, k, v) => setCorrectItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: k === "name" ? v : Number(v) } : it)));
  const delCI = (i) => setCorrectItems((arr) => arr.filter((_, idx) => idx !== i));
  const openCorrect = (inv) => {
    setCorrectInv(inv);
    setCorrectItems((inv?.items || []).map((it) => ({ name: it.name, price: it.price, quantity: it.quantity, gst_rate: it.gst_rate, hsn: it.hsn || "" })));
    setCorrectReason("");
  };
  const saveCorrect = async () => {
    try {
      await api.post(`/invoices/${correctInv.id}/correct`, { items: correctItems, reason: correctReason });
      toast.success("Invoice corrected");
      setCorrectInv(null);
      await loadAll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to correct invoice");
    }
  };
  const logoRef = useRef();
  const menuImgRef = useRef();
  const [imgUploading, setImgUploading] = useState(false);

  const uploadMenuImg = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error("Image too large (max 10MB)"); return; }
    setImgUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setImgUploading(false); }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd);
      setCfg((c) => ({ ...c, logo_url: data.url }));
      await api.put("/settings", { logo_url: data.url });
      reload(); toast.success("Logo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const [userDlg, setUserDlg] = useState(null);
  const [menuDlg, setMenuDlg] = useState(null);
  const [invDlg, setInvDlg] = useState(null);
  const [form, setForm] = useState({});
  const [cfg, setCfg] = useState({});

  const loadAll = async () => {
    try {
      const [s, u, m, i, inv, a, o, g, ct] = await Promise.all([
        api.get("/analytics/summary"), api.get("/users"), api.get("/menu"),
        api.get("/inventory"), api.get("/invoices"), api.get("/audit"), api.get("/orders"),
        api.get("/customer-groups"), api.get("/charge-types"),
      ]);
      setSummary(s.data); setUsers(u.data); setMenu(m.data);
      setInventory(i.data); setInvoices(inv.data); setAudit(a.data); setOrders(o.data);
      setGroups(g.data); setChargeTypes(ct.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadAll(); }, []);
  useEffect(() => { setCfg(settings); }, [settings]);

  // ---- Users ----
  const openUser = (u) => { setForm(u ? { ...u, password: "" } : { role: "member", active: true, credit_limit: settings.default_credit_limit || 0 }); setUserDlg(u ? "edit" : "new"); };
  const saveUser = async () => {
    try {
      if (userDlg === "new") await api.post("/users", form);
      else await api.put(`/users/${form.id}`, form);
      toast.success("User saved"); setUserDlg(null); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deactivateUser = async (u) => { try { await api.delete(`/users/${u.id}`); toast.success("Deactivated"); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const resetPw = async (u) => {
    try {
      const { data } = await api.post(`/users/${u.id}/reset-password`);
      navigator.clipboard?.writeText(data.temp_password).catch(() => {});
      toast.success(`Temp password for ${u.name}: ${data.temp_password}`, { duration: 15000, description: "Copied to clipboard. Share it securely." });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Menu ----
  const openMenu = (m) => { setForm(m ? { ...m, chargesMap: chargesToMap(m.charges), available_groups: m.available_groups || [] } : { category: "Mains", price: 0, hidden: false, stock: 0, reorder_level: 5, chargesMap: {}, min_order_qty: 1, available_groups: [] }); setMenuDlg(m ? "edit" : "new"); };
  const saveMenu = async () => {
    try {
      const payload = { ...form, price: Number(form.price),
        stock: Number(form.stock) || 0, reorder_level: Number(form.reorder_level) || 0,
        gst_rate: (form.gst_rate === "" || form.gst_rate == null) ? null : Number(form.gst_rate),
        discount_percent: Number(form.discount_percent) || 0,
        min_order_qty: Math.max(1, parseInt(form.min_order_qty, 10) || 1),
        available_groups: (form.available_groups || []).filter(Boolean),
        charges: buildCharges(itemChargeTypes, form.chargesMap) };
      delete payload.chargesMap;
      if (menuDlg === "new") await api.post("/menu", payload);
      else await api.put(`/menu/${form.id}`, payload);
      toast.success("Menu item saved"); setMenuDlg(null); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const hideMenu = async (m) => {
    try { await api.post(`/menu/${m.id}/visibility`, { hidden: !m.hidden }); toast.success(m.hidden ? "Unhidden" : "Hidden"); loadAll(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Inventory (stock of menu items) ----
  const openInv = (it) => { setForm({ ...it }); setInvDlg("edit"); };
  const saveInv = async () => {
    try {
      await api.put(`/inventory/${form.id}`, { quantity: Number(form.quantity), reorder_level: Number(form.low_stock_threshold) });
      toast.success("Stock updated"); setInvDlg(null); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doReset = async () => {
    setResetting(true);
    try {
      await api.post("/admin/master-reset", { confirm: resetText.trim() });
      toast.success("Master data reset complete");
      setResetDlg(false); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setResetting(false); }
  };

  // ---- Invoices ----
  const cancelInvoice = async (inv) => { try { await api.post(`/invoices/${inv.id}/cancel`); toast.success("Invoice cancelled"); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const collectPayment = async () => { try { await api.post(`/invoices/${payFor.id}/pay`, { amount: Number(payAmt), mode: payMode, remarks: payRemarks }); toast.success("Payment recorded"); setPayFor(null); setPayAmt(""); setPayRemarks(""); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  // ---- Settings ----
  const saveCfg = async () => {
    try {
      const payload = { club_name: cfg.club_name, address: cfg.address, gst_enabled: cfg.gst_enabled,
        gst_registered: cfg.gst_registered, gst_mode: cfg.gst_mode, gstin: cfg.gstin,
        gst_rate: Number(cfg.gst_rate),
        default_credit_limit: Number(cfg.default_credit_limit),
        max_active_orders: Number(cfg.max_active_orders), invoice_prefix: cfg.invoice_prefix,
        order_prefix: cfg.order_prefix, expense_prefix: cfg.expense_prefix, stock_adj_prefix: cfg.stock_adj_prefix,
        club_open_time: cfg.club_open_time, club_close_time: cfg.club_close_time,
        auto_hours_enabled: !!cfg.auto_hours_enabled, club_timezone: cfg.club_timezone || "Asia/Kolkata",
        financial_year: cfg.financial_year, invoice_footer: cfg.invoice_footer };
      await api.put("/settings", payload); toast.success("Settings saved"); reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Customer Groups & Charge Types ----
  const itemChargeTypes = chargeTypes.filter((t) => t.scope === "item" && t.active !== false);
  const groupChargeTypes = chargeTypes.filter((t) => t.scope === "group" && t.active !== false);
  const chargesToMap = (charges) => Object.fromEntries((charges || []).map((c) => [c.charge_type_id, { mode: c.mode, value: c.value }]));
  const buildCharges = (types, map) => types.map((t) => { const e = (map || {})[t.id] || {}; return { charge_type_id: t.id, name: t.name, mode: e.mode || "flat", value: Number(e.value) || 0 }; }).filter((c) => c.value > 0);

  const openGroup = (g) => { setGroupForm(g ? { ...g, payment_modes: g.payment_modes || { cod: true, credit: true, pay_now: !!g.online_payment_enabled }, chargesMap: chargesToMap(g.charges) } : { name: "", active: true, payment_modes: { cod: true, credit: true, pay_now: false }, chargesMap: {} }); setGroupDlg(g ? "edit" : "new"); };
  const setGroupMode = (k, v) => setGroupForm((f) => ({ ...f, payment_modes: { ...(f.payment_modes || { cod: true, credit: true, pay_now: false }), [k]: v } }));
  const setGroupCharge = (tid, k, v) => setGroupForm((f) => ({ ...f, chargesMap: { ...(f.chargesMap || {}), [tid]: { ...((f.chargesMap || {})[tid] || { mode: "flat", value: 0 }), [k]: v } } }));
  const saveGroup = async () => {
    try {
      const pm = groupForm.payment_modes || { cod: true, credit: true, pay_now: false };
      if (!pm.cod && !pm.credit && !pm.pay_now) { toast.error("Enable at least one payment method"); return; }
      const body = { name: groupForm.name, active: groupForm.active !== false, payment_modes: pm, discount_percent: Number(groupForm.discount_percent) || 0, charges: buildCharges(groupChargeTypes, groupForm.chargesMap) };
      if (groupDlg === "new") await api.post("/customer-groups", body); else await api.put(`/customer-groups/${groupForm.id}`, body);
      toast.success("Customer group saved"); setGroupDlg(null); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteGroup = async (g) => { if (!window.confirm(`Delete customer group "${g.name}"? This cannot be undone.`)) return; try { await api.delete(`/customer-groups/${g.id}`); toast.success("Group deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openCharge = (c, scope) => { setChargeForm(c ? { ...c } : { name: "", scope: scope || "item", active: true }); setChargeDlg(c ? "edit" : "new"); };
  const saveCharge = async () => {
    try {
      const body = { name: chargeForm.name, scope: chargeForm.scope || "item", active: chargeForm.active !== false };
      if (chargeDlg === "new") await api.post("/charge-types", body); else await api.put(`/charge-types/${chargeForm.id}`, body);
      toast.success("Charge type saved"); setChargeDlg(null); loadAll();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const deleteCharge = async (c) => { if (!window.confirm(`Delete charge type "${c.name}"? This cannot be undone.`)) return; try { await api.delete(`/charge-types/${c.id}`); toast.success("Charge type deleted"); loadAll(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const nav = [
    { key: "overview", label: "Dashboard", icon: LayoutDashboard },
    { key: "users", label: "Members & Staff", icon: Users },
    { key: "groups", label: "Customer Groups", icon: Tags },
    { key: "charges", label: "Charge Types", icon: Coins },
    { key: "menu", label: "Menu", icon: UtensilsCrossed },
    { key: "inventory", label: "Inventory", icon: Boxes },
    { key: "orders", label: "Live Orders", icon: ShoppingCart },
    { key: "invoices", label: "Invoices", icon: ReceiptText },
    { key: "expenses", label: "Expenses", icon: Receipt },
    { key: "reports", label: "Reports & Exports", icon: BarChart3 },
    { key: "operations", label: "Club Operations", icon: CalendarClock },
    { key: "settings", label: "System Settings", icon: SettingsIcon },
    { key: "audit", label: "Audit Log", icon: ScrollText },
  ];

  const field = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (loading) return <Shell navItems={nav} active={tab} onNav={setTab} accent="admin"><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-brand-primary" /></div></Shell>;

  return (
    <Shell navItems={nav} active={tab} onNav={setTab} accent="admin">
      <div className="px-5 lg:px-8 py-6 lg:py-8">
        {/* OVERVIEW */}
        {tab === "overview" && summary && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Executive</p>
                <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text">Dashboard</h1>
              </div>
              <span className={`text-xs font-semibold px-3 py-1.5 rounded-sm ${summary.club_open ? "bg-brand-ready text-white" : "bg-brand-alert text-white"}`}>{summary.club_open ? "Club Open" : "Club Closed"}</span>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 divide-x divide-y lg:divide-y-0 divide-brand-line border border-brand-line rounded-md bg-white mb-6">
              {[
                { l: "Revenue", v: money(summary.total_revenue, sym), icon: TrendingUp },
                { l: "Collected", v: money(summary.collected, sym), icon: Wallet },
                { l: "Outstanding", v: money(summary.outstanding_total, sym), icon: Wallet },
                { l: "Active Orders", v: summary.pending_orders, icon: ShoppingCart },
              ].map((m, i) => (
                <div key={i} className="p-6" data-testid={`metric-${m.l.toLowerCase().replace(" ", "-")}`}>
                  <div className="flex items-center gap-2 text-brand-muted"><m.icon className="w-4 h-4" /><span className="text-xs uppercase tracking-wider">{m.l}</span></div>
                  <p className="font-serif text-3xl tracking-tight text-brand-text mt-2">{m.v}</p>
                </div>
              ))}
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-white border border-brand-line rounded-md p-5">
                <h3 className="font-semibold text-brand-text mb-4">Revenue — Last 7 Days</h3>
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={summary.revenue_by_day}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 12, fill: "#5C6B64" }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 12, fill: "#5C6B64" }} axisLine={false} tickLine={false} />
                    <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E0D8", fontSize: 12 }} formatter={(v) => money(v, sym)} />
                    <Bar dataKey="revenue" fill="#0F291E" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-6">
                <div className="bg-white border border-brand-line rounded-md p-5">
                  <h3 className="font-semibold text-brand-text mb-3">Popular Items</h3>
                  {summary.popular_items.length === 0 ? <p className="text-sm text-brand-muted">No data.</p> :
                    summary.popular_items.map((p, i) => (
                      <div key={i} className="flex justify-between text-sm py-1.5 border-b border-brand-line last:border-0"><span>{p.name}</span><span className="font-semibold">{p.count}</span></div>
                    ))}
                </div>
                <div className="bg-white border border-brand-line rounded-md p-5">
                  <h3 className="font-semibold text-brand-text mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-brand-alert" /> Low Stock</h3>
                  {summary.low_stock_items.length === 0 ? <p className="text-sm text-brand-muted">All stocked.</p> :
                    summary.low_stock_items.map((it) => (
                      <div key={it.id} className="flex justify-between text-sm py-1.5 border-b border-brand-line last:border-0"><span>{it.name}</span><span className="text-brand-alert font-semibold">{it.quantity} {it.unit}</span></div>
                    ))}
                </div>
              </div>
            </div>
          </>
        )}

        {/* USERS */}
        {tab === "users" && (
          <Section title="Members & Staff" onAdd={() => openUser(null)} addLabel="Add User" addTestId="add-user-button">
            <div className="flex items-center gap-2 p-3 border-b border-brand-line bg-brand-bg/40">
              <span className="text-xs uppercase tracking-wider text-brand-muted">Filter by Group</span>
              <Select value={userGroupFilter} onValueChange={setUserGroupFilter}>
                <SelectTrigger className="border-brand-line h-9 w-56 bg-white" data-testid="user-group-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Groups</SelectItem>
                  <SelectItem value="none">Unassigned</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                {["Name", "User ID", "Email", "Role", "Group", "Credit Limit", "Outstanding", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {users.filter((u) => userGroupFilter === "all" || (userGroupFilter === "none" ? !u.customer_group_id : u.customer_group_id === userGroupFilter)).map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="hover:bg-brand-bg/50">
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="font-mono text-xs">{u.username}</TableCell>
                    <TableCell className="text-brand-muted">{u.email || <span className="text-brand-line">—</span>}</TableCell>
                    <TableCell><span className="text-xs uppercase font-semibold tracking-wider">{u.role}</span></TableCell>
                    <TableCell className="text-sm">{u.customer_group || <span className="text-brand-muted">—</span>}</TableCell>
                    <TableCell>{u.role === "member" ? money(u.credit_limit, sym) : "—"}</TableCell>
                    <TableCell>{u.role === "member" ? money(u.outstanding, sym) : "—"}</TableCell>
                    <TableCell>{u.active ? <span className="text-brand-ready text-xs font-semibold">Active</span> : <span className="text-brand-alert text-xs font-semibold">Inactive</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => resetPw(u)} data-testid={`reset-user-${u.id}`} title="Reset password" className="w-8 h-8 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><KeyRound className="w-3.5 h-3.5" /></button>
                        <button onClick={() => openUser(u)} data-testid={`edit-user-${u.id}`} className="w-8 h-8 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deactivateUser(u)} data-testid={`deactivate-user-${u.id}`} className="w-8 h-8 border border-brand-alert/40 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* CUSTOMER GROUPS */}
        {tab === "groups" && (
          <Section title="Customer Groups" onAdd={() => openGroup(null)} addLabel="Add Group" addTestId="add-group-button">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                {["Group", "Members", "Group Charges", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {groups.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-brand-muted py-10">No customer groups yet.</TableCell></TableRow>}
                {groups.map((g) => (
                  <TableRow key={g.id} data-testid={`group-row-${g.id}`} className="hover:bg-brand-bg/50">
                    <TableCell className="font-medium">{g.name}</TableCell>
                    <TableCell className="text-brand-muted">{users.filter((u) => u.customer_group_id === g.id).length}</TableCell>
                    <TableCell className="text-sm text-brand-muted max-w-[320px]">
                      {(g.charges || []).length === 0 ? "—" : g.charges.map((c) => `${c.name}: ${c.mode === "percent" ? c.value + "%" : money(c.value, sym)}`).join(", ")}
                    </TableCell>
                    <TableCell>{g.active !== false ? <span className="text-brand-ready text-xs font-semibold">Active</span> : <span className="text-brand-alert text-xs font-semibold">Inactive</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openGroup(g)} data-testid={`edit-group-${g.id}`} className="w-8 h-8 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteGroup(g)} data-testid={`delete-group-${g.id}`} className="w-8 h-8 border border-brand-alert/40 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* CHARGE TYPES */}
        {tab === "charges" && (
          <Section title="Charge Types" onAdd={() => openCharge(null, "item")} addLabel="Add Charge Type" addTestId="add-charge-button">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                {["Charge Name", "Scope", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {chargeTypes.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-brand-muted py-10">No charge types yet.</TableCell></TableRow>}
                {chargeTypes.map((c) => (
                  <TableRow key={c.id} data-testid={`charge-row-${c.id}`} className="hover:bg-brand-bg/50">
                    <TableCell className="font-medium">{c.name}</TableCell>
                    <TableCell><span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-sm ${c.scope === "item" ? "bg-brand-gold/15 text-brand-gold" : "bg-brand-primary/10 text-brand-primary"}`}>{c.scope === "item" ? "Item-level" : "Group-level"}</span></TableCell>
                    <TableCell>{c.active !== false ? <span className="text-brand-ready text-xs font-semibold">Active</span> : <span className="text-brand-alert text-xs font-semibold">Inactive</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openCharge(c)} data-testid={`edit-charge-${c.id}`} className="w-8 h-8 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><Pencil className="w-3.5 h-3.5" /></button>
                        <button onClick={() => deleteCharge(c)} data-testid={`delete-charge-${c.id}`} className="w-8 h-8 border border-brand-alert/40 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* MENU */}
        {tab === "menu" && (
          <Section title="Menu Management" onAdd={() => openMenu(null)} addLabel="Add Item" addTestId="add-menu-button">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                {["Item", "Category", "Price", "Availability", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {menu.map((m) => (
                  <TableRow key={m.id} data-testid={`menu-row-${m.id}`} className={m.hidden ? "opacity-50 hover:bg-brand-bg/50" : "hover:bg-brand-bg/50"}>
                    <TableCell className="font-medium">{m.name}</TableCell>
                    <TableCell className="text-brand-muted">{m.category}</TableCell>
                    <TableCell>{money(m.price, sym)}</TableCell>
                    <TableCell>{m.hidden ? <span className="text-xs text-brand-muted">Hidden</span> : m.available ? <span className="text-brand-ready text-xs font-semibold">Available</span> : <span className="text-brand-alert text-xs font-semibold">Out of Stock</span>}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => openMenu(m)} data-testid={`edit-menu-${m.id}`} className="w-8 h-8 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><Pencil className="w-3.5 h-3.5" /></button>
                        {m.hidden ? (
                          <button onClick={() => hideMenu(m)} data-testid={`unhide-menu-${m.id}`} title="Unhide" className="w-8 h-8 border border-brand-ready/40 text-brand-ready rounded-sm flex items-center justify-center hover:bg-brand-ready/10"><Eye className="w-3.5 h-3.5" /></button>
                        ) : (
                          <button onClick={() => hideMenu(m)} data-testid={`hide-menu-${m.id}`} title="Hide" className="w-8 h-8 border border-brand-alert/40 text-brand-alert rounded-sm flex items-center justify-center hover:bg-brand-alert/10"><EyeOff className="w-3.5 h-3.5" /></button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* INVENTORY */}
        {tab === "inventory" && (
          <Section title="Inventory · Menu Item Stock">
            <Table>
              <TableHeader><TableRow className="hover:bg-transparent">
                {["Menu Item", "Category", "Stock", "Status", "Reorder", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
              </TableRow></TableHeader>
              <TableBody>
                {inventory.map((it) => (
                  <TableRow key={it.id} data-testid={`admin-inv-row-${it.id}`} className={it.quantity <= it.low_stock_threshold ? "bg-brand-alert/5" : "hover:bg-brand-bg/50"}>
                    <TableCell className="font-medium">{it.name}</TableCell>
                    <TableCell className="text-brand-muted">{it.category}</TableCell>
                    <TableCell>{it.quantity}</TableCell>
                    <TableCell>{it.quantity <= 0 ? <span className="text-brand-alert text-xs font-semibold">Out of Stock</span> : <span className="text-brand-ready text-xs font-semibold">Available</span>}</TableCell>
                    <TableCell>{it.low_stock_threshold}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button onClick={() => setAdjItem(it)} data-testid={`adjust-inv-${it.id}`} variant="outline" className="h-8 rounded-sm border-brand-line text-xs">Adjust</Button>
                        <button onClick={() => openInv(it)} data-testid={`edit-inv-${it.id}`} className="w-8 h-8 border border-brand-line rounded-sm flex items-center justify-center hover:bg-brand-bg"><Pencil className="w-3.5 h-3.5" /></button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Section>
        )}

        {/* LIVE ORDERS */}
        {tab === "orders" && (
          <>
            <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-brand-gold mb-1">Service Floor</p>
                <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text">Live Orders</h1>
              </div>
              <Button onClick={loadAll} variant="outline" data-testid="refresh-orders-admin" className="rounded-sm border-brand-line">Refresh</Button>
            </div>
            <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  {["Order", "Member", "Address", "Items", "Total", "Payment", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {orders.filter((o) => ["placed", "accepted", "preparing", "ready", "out_for_delivery"].includes(o.status)).length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center text-brand-muted py-10">No active orders right now.</TableCell></TableRow>
                  )}
                  {orders.filter((o) => ["placed", "accepted", "preparing", "ready", "out_for_delivery"].includes(o.status)).map((o) => (
                    <TableRow key={o.id} data-testid={`admin-order-${o.id}`} className="hover:bg-brand-bg/50">
                      <TableCell className="font-medium">{o.order_number}<p className="text-[11px] text-brand-muted font-normal">{fmtDate(o.created_at)}</p></TableCell>
                      <TableCell>{o.member_name}</TableCell>
                      <TableCell className="text-sm text-brand-muted max-w-[180px]">{o.member_address || "—"}</TableCell>
                      <TableCell className="text-sm text-brand-muted max-w-[220px]">{o.items.map((it) => `${it.quantity}× ${it.name}`).join(", ")}</TableCell>
                      <TableCell>{money(o.total, sym)}</TableCell>
                      <TableCell><span className="text-[10px] uppercase tracking-wider text-brand-gold font-semibold">{o.payment_mode}</span></TableCell>
                      <TableCell><StatusBadge status={o.status} /></TableCell>
                      <TableCell className="text-right">
                        <Button onClick={() => setEditOrder(o)} data-testid={`edit-order-${o.id}`} variant="outline" className="h-8 rounded-sm border-brand-line text-xs gap-1"><Pencil className="w-3.5 h-3.5" /> Edit</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {/* INVOICES */}
        {tab === "invoices" && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-6">Invoices</h1>
            <div className="bg-white border border-brand-line rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  {["Invoice", "Member", "Total", "Paid", "Status", ""].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {invoices.map((inv) => (
                    <TableRow key={inv.id} data-testid={`admin-invoice-${inv.id}`} className="hover:bg-brand-bg/50">
                      <TableCell className="font-medium">{inv.invoice_number}<p className="text-[11px] text-brand-muted font-normal">{fmtDate(inv.created_at)}</p></TableCell>
                      <TableCell>{inv.member_name}</TableCell>
                      <TableCell>{money(inv.total, sym)}</TableCell>
                      <TableCell>{money(inv.amount_paid, sym)}</TableCell>
                      <TableCell><StatusBadge status={inv.cancelled ? "cancelled" : inv.status} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {!inv.cancelled && inv.balance_due > 0 && inv.payment_mode !== "pay_now" && (
                            <Button onClick={() => { setPayFor(inv); setPayAmt(String(inv.balance_due)); setPayMode("cash"); setPayRemarks(""); }} data-testid={`collect-${inv.id}`} className="h-8 bg-brand-gold hover:bg-brand-gold-hover text-white rounded-sm text-xs">Collect</Button>
                          )}
                          <Button onClick={() => setViewInv(inv)} variant="outline" className="h-8 rounded-sm border-brand-line text-xs">Print</Button>
                          {!inv.cancelled && <Button onClick={() => openCorrect(inv)} data-testid={`correct-invoice-${inv.id}`} variant="outline" className="h-8 rounded-sm border-brand-line text-xs">Correct</Button>}
                          {!inv.cancelled && <Button onClick={() => cancelInvoice(inv)} data-testid={`cancel-invoice-${inv.id}`} className="h-8 bg-brand-alert hover:bg-brand-alert/90 text-white rounded-sm text-xs">Cancel</Button>}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}

        {tab === "expenses" && <ExpensesPanel role="admin" />}
        {tab === "reports" && <ReportsPanel />}
        {tab === "operations" && <ClubOpsPanel role="admin" onViewPending={() => setTab("orders")} />}

        {/* SETTINGS */}
        {tab === "settings" && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-6">System Settings</h1>
            <div className="bg-white border border-brand-line rounded-md p-6 max-w-3xl space-y-5">
              <div className="flex items-center gap-4 border border-brand-line rounded-sm p-4">
                {cfg.logo_url
                  ? <img src={resolveUrl(cfg.logo_url)} alt="logo" className="w-16 h-16 rounded-sm object-cover bg-brand-bg" data-testid="settings-logo-preview" />
                  : <div className="w-16 h-16 rounded-sm bg-brand-bg flex items-center justify-center text-brand-muted text-xs">No logo</div>}
                <div className="flex-1">
                  <p className="font-semibold text-sm">Club Logo</p>
                  <p className="text-xs text-brand-muted mb-2">Shown on the portal and every printed invoice.</p>
                  <input ref={logoRef} type="file" accept="image/*" className="hidden" data-testid="logo-file-input" onChange={(e) => uploadLogo(e.target.files[0])} />
                  <Button type="button" variant="outline" onClick={() => logoRef.current?.click()} data-testid="upload-logo-button" className="rounded-sm border-brand-line gap-2"><Upload className="w-4 h-4" /> Upload Logo</Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Fld label="Club Name" testid="cfg-club-name"><Input value={cfg.club_name || ""} onChange={(e) => setCfg({ ...cfg, club_name: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Financial Year" testid="cfg-fy"><Input value={cfg.financial_year || ""} onChange={(e) => setCfg({ ...cfg, financial_year: e.target.value })} className="border-brand-line" /></Fld>
              </div>
              <Fld label="Address" testid="cfg-address"><Input value={cfg.address || ""} onChange={(e) => setCfg({ ...cfg, address: e.target.value })} className="border-brand-line" /></Fld>
              <div className="flex items-center justify-between border border-brand-line rounded-sm p-4">
                <div><p className="font-semibold text-sm">GST Registered</p><p className="text-xs text-brand-muted">Enable GST features and invoicing</p></div>
                <Switch checked={!!cfg.gst_registered} onCheckedChange={(v) => setCfg({ ...cfg, gst_registered: v, gst_enabled: v })} data-testid="cfg-gst-registered" />
              </div>
              {!cfg.gst_registered ? (
                <p className="text-sm text-brand-muted bg-brand-bg border border-brand-line rounded-sm p-3" data-testid="gst-disabled-msg">GST is currently disabled because the club is not GST registered.</p>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Fld label="GSTIN" testid="cfg-gstin"><Input value={cfg.gstin || ""} onChange={(e) => setCfg({ ...cfg, gstin: e.target.value })} className="border-brand-line" /></Fld>
                    <Fld label="GST Type" testid="cfg-gst-mode">
                      <Select value={cfg.gst_mode || "intra"} onValueChange={(v) => setCfg({ ...cfg, gst_mode: v })}>
                        <SelectTrigger className="border-brand-line" data-testid="cfg-gst-mode-trigger"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="intra">CGST + SGST (Intra-state)</SelectItem>
                          <SelectItem value="inter">IGST only (Inter-state)</SelectItem>
                        </SelectContent>
                      </Select>
                    </Fld>
                    <Fld label="GST Rate %" testid="cfg-gst-rate"><Input type="number" value={cfg.gst_rate ?? 0} onChange={(e) => setCfg({ ...cfg, gst_rate: e.target.value })} className="border-brand-line" /></Fld>
                  </div>
                </>
              )}
              <div className="border border-brand-line rounded-sm p-3 bg-brand-bg/40" data-testid="charges-moved-note">
                <p className="text-sm text-brand-text">Service, packaging, delivery &amp; miscellaneous charges are now dynamic. Define them under <b>Charge Types</b>, then assign rates on individual <b>Menu Items</b> (item-level) and <b>Customer Groups</b> (group-level).</p>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Fld label="Max Active Orders" testid="cfg-max-orders"><Input type="number" value={cfg.max_active_orders ?? 3} onChange={(e) => setCfg({ ...cfg, max_active_orders: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Default Credit Limit" testid="cfg-credit"><Input type="number" value={cfg.default_credit_limit ?? 0} onChange={(e) => setCfg({ ...cfg, default_credit_limit: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Club Open Time" testid="cfg-open-time"><Input type="time" value={cfg.club_open_time || ""} onChange={(e) => setCfg({ ...cfg, club_open_time: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Club Close Time" testid="cfg-close-time"><Input type="time" value={cfg.club_close_time || ""} onChange={(e) => setCfg({ ...cfg, club_close_time: e.target.value })} className="border-brand-line" /></Fld>
              </div>
              <div className="flex items-center justify-between border border-brand-line rounded-sm p-4">
                <div><p className="font-semibold text-sm">Automatic Hours</p><p className="text-xs text-brand-muted">Auto-open at the open time and auto-close at the close time. Staff can still override manually until the next scheduled change.</p></div>
                <Switch checked={!!cfg.auto_hours_enabled} onCheckedChange={(v) => setCfg({ ...cfg, auto_hours_enabled: v })} data-testid="cfg-auto-hours" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <Fld label="Order Prefix" testid="cfg-order-prefix"><Input value={cfg.order_prefix || ""} onChange={(e) => setCfg({ ...cfg, order_prefix: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Invoice Prefix" testid="cfg-invoice-prefix"><Input value={cfg.invoice_prefix || ""} onChange={(e) => setCfg({ ...cfg, invoice_prefix: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Expense Prefix" testid="cfg-expense-prefix"><Input value={cfg.expense_prefix || ""} onChange={(e) => setCfg({ ...cfg, expense_prefix: e.target.value })} className="border-brand-line" /></Fld>
                <Fld label="Stock Adj Prefix" testid="cfg-sa-prefix"><Input value={cfg.stock_adj_prefix || ""} onChange={(e) => setCfg({ ...cfg, stock_adj_prefix: e.target.value })} className="border-brand-line" /></Fld>
              </div>
              <Fld label="Invoice Footer" testid="cfg-footer"><Textarea value={cfg.invoice_footer || ""} onChange={(e) => setCfg({ ...cfg, invoice_footer: e.target.value })} className="border-brand-line" /></Fld>
              <Button onClick={saveCfg} data-testid="save-settings-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm">Save Settings</Button>
            </div>

            <div className="mt-8 border border-brand-alert/40 bg-brand-alert/5 rounded-md p-5" data-testid="danger-zone">
              <div className="flex items-center gap-2 mb-1"><AlertTriangle className="w-4 h-4 text-brand-alert" /><h3 className="font-semibold text-brand-alert">Danger Zone</h3></div>
              <p className="text-sm text-brand-muted mb-4">Reset all transaction data for a fresh start. Users, menu items, categories and club settings are kept.</p>
              <Button onClick={() => { setResetText(""); setResetDlg(true); }} data-testid="master-reset-button" variant="outline" className="rounded-sm border-brand-alert/50 text-brand-alert hover:bg-brand-alert/10 gap-2"><Trash2 className="w-4 h-4" /> Master Data Reset</Button>
            </div>
          </>
        )}

        {/* AUDIT */}
        {tab === "audit" && (
          <>
            <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text mb-6">Audit Log</h1>
            <div className="bg-white border border-brand-line rounded-md overflow-hidden">
              <Table>
                <TableHeader><TableRow className="hover:bg-transparent">
                  {["Time", "Actor", "Action", "Entity", "Details"].map((h, i) => <TableHead key={i} className="text-xs uppercase tracking-wider">{h}</TableHead>)}
                </TableRow></TableHeader>
                <TableBody>
                  {audit.map((a) => (
                    <TableRow key={a.id} className="hover:bg-brand-bg/50" data-testid={`audit-${a.id}`}>
                      <TableCell className="text-xs text-brand-muted whitespace-nowrap">{fmtDate(a.timestamp)}</TableCell>
                      <TableCell className="text-sm">{a.actor_name} <span className="text-xs text-brand-muted">({a.actor_role})</span></TableCell>
                      <TableCell><span className="text-xs uppercase font-semibold tracking-wider">{a.action}</span></TableCell>
                      <TableCell className="text-sm text-brand-muted">{a.entity_type}</TableCell>
                      <TableCell className="text-sm">{a.details}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* USER DIALOG */}
      <Dialog open={!!userDlg} onOpenChange={(o) => !o && setUserDlg(null)}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">{userDlg === "new" ? "Add User" : "Edit User"}</DialogTitle><DialogDescription className="sr-only">Manage member and staff accounts</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Fld label="Name" testid="user-name"><Input value={form.name || ""} onChange={(e) => field("name", e.target.value)} className="border-brand-line" /></Fld>
            <Fld label="User ID (Username)" testid="user-username"><Input type="text" autoCapitalize="none" autoCorrect="off" value={form.username || ""} onChange={(e) => field("username", e.target.value)} disabled={userDlg === "edit"} placeholder="e.g. nimik.riba" className="border-brand-line" /></Fld>
            <Fld label="Email (optional)" testid="user-email"><Input type="email" autoCapitalize="none" autoCorrect="off" value={form.email || ""} onChange={(e) => field("email", e.target.value)} placeholder="name@example.com" className="border-brand-line" /></Fld>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Role" testid="user-role">
                <Select value={form.role} onValueChange={(v) => field("role", v)}>
                  <SelectTrigger className="border-brand-line" data-testid="user-role-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="member">Member</SelectItem><SelectItem value="staff">Staff</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                </Select>
              </Fld>
              <Fld label="Member No/GSTIN (Buyer)" testid="user-code"><Input value={form.member_code || ""} onChange={(e) => field("member_code", e.target.value)} className="border-brand-line" /></Fld>
            </div>
            <Fld label="Address" testid="user-address"><Input value={form.address || ""} onChange={(e) => field("address", e.target.value)} placeholder="House / street, area, city" className="border-brand-line" /></Fld>
            <Fld label="Customer Group" testid="user-group">
              <Select value={form.customer_group_id || "none"} onValueChange={(v) => field("customer_group_id", v === "none" ? "" : v)}>
                <SelectTrigger className="border-brand-line" data-testid="user-group-trigger"><SelectValue placeholder="Select group" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {groups.map((g) => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Fld>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Phone" testid="user-phone"><Input value={form.phone || ""} onChange={(e) => field("phone", e.target.value)} className="border-brand-line" /></Fld>
              <Fld label="Credit Limit" testid="user-credit"><Input type="number" value={form.credit_limit ?? 0} onChange={(e) => field("credit_limit", Number(e.target.value))} className="border-brand-line" /></Fld>
            </div>
            <Fld label={userDlg === "new" ? "Password" : "New Password (optional)"} testid="user-password"><Input type="password" value={form.password || ""} onChange={(e) => field("password", e.target.value)} className="border-brand-line" /></Fld>
            <div className="flex items-center gap-2"><Switch checked={form.active !== false} onCheckedChange={(v) => field("active", v)} data-testid="user-active" /><span className="text-sm">Active</span></div>
          </div>
          <DialogFooter><Button onClick={saveUser} data-testid="save-user-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* MENU DIALOG */}
      <Dialog open={!!menuDlg} onOpenChange={(o) => !o && setMenuDlg(null)}>
        <DialogContent className="bg-brand-bg max-w-lg max-h-[90vh] overflow-y-auto" data-testid="menu-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">{menuDlg === "new" ? "Add Menu Item" : "Edit Menu Item"}</DialogTitle><DialogDescription className="sr-only">Manage menu item details and stock</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Fld label="Name" testid="menu-name"><Input value={form.name || ""} onChange={(e) => field("name", e.target.value)} className="border-brand-line" /></Fld>
            <Fld label="Description" testid="menu-desc"><Textarea value={form.description || ""} onChange={(e) => field("description", e.target.value)} className="border-brand-line" /></Fld>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Category" testid="menu-cat"><Input value={form.category || ""} onChange={(e) => field("category", e.target.value)} className="border-brand-line" /></Fld>
              <Fld label="Price" testid="menu-price"><Input type="number" value={form.price ?? 0} onChange={(e) => field("price", e.target.value)} className="border-brand-line" /></Fld>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Item Image</Label>
              <div className="flex items-center gap-3">
                {form.image_url
                  ? <img src={resolveUrl(form.image_url)} alt="item" className="w-20 h-20 rounded-sm object-cover bg-brand-bg border border-brand-line" data-testid="menu-image-preview" />
                  : <div className="w-20 h-20 rounded-sm bg-brand-bg border border-brand-line flex items-center justify-center text-brand-muted text-[10px] text-center px-1">No image</div>}
                <div className="flex flex-col gap-2">
                  <input ref={menuImgRef} type="file" accept="image/*" className="hidden" data-testid="menu-image-input" onChange={(e) => uploadMenuImg(e.target.files[0])} />
                  <Button type="button" variant="outline" onClick={() => menuImgRef.current?.click()} disabled={imgUploading} data-testid="upload-menu-image-button" className="rounded-sm border-brand-line gap-2">
                    {imgUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Upload from device
                  </Button>
                  {form.image_url && <button type="button" onClick={() => field("image_url", "")} data-testid="menu-image-remove" className="text-xs text-brand-alert hover:underline text-left">Remove image</button>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="GST Rate % (blank = default)" testid="menu-gst"><Input type="number" value={form.gst_rate ?? ""} onChange={(e) => field("gst_rate", e.target.value)} className="border-brand-line" placeholder="use club default" /></Fld>
              <Fld label="HSN / SAC" testid="menu-hsn"><Input value={form.hsn || ""} onChange={(e) => field("hsn", e.target.value)} className="border-brand-line" /></Fld>
            </div>
            <Fld label="Item Discount %" testid="menu-discount"><Input type="number" min={0} max={100} value={form.discount_percent ?? ""} placeholder="0" onChange={(e) => field("discount_percent", e.target.value)} className="border-brand-line" /></Fld>
            <Fld label="Minimum Order Qty" testid="menu-moq"><Input type="number" min={1} value={form.min_order_qty ?? 1} placeholder="1" onChange={(e) => field("min_order_qty", e.target.value)} className="border-brand-line" /></Fld>
            <div>
              <Label className="text-xs text-brand-muted mb-1 block">Customer Group Availability</Label>
              <div className="border border-brand-line rounded-sm p-3 max-h-40 overflow-y-auto space-y-2 bg-brand-bg/40" data-testid="menu-group-availability">
                <p className="text-[11px] text-brand-muted">Leave all unchecked for All Groups.</p>
                {groups.filter((g) => g.active !== false).map((g) => (
                  <label key={g.id} className="flex items-center gap-2 text-sm text-brand-text cursor-pointer">
                    <input type="checkbox" data-testid={`menu-group-chk-${g.id}`}
                      checked={(form.available_groups || []).includes(g.id)}
                      onChange={(e) => {
                        const cur = form.available_groups || [];
                        field("available_groups", e.target.checked ? [...cur, g.id] : cur.filter((x) => x !== g.id));
                      }}
                      className="accent-brand-primary" />
                    {g.name}
                  </label>
                ))}
                {groups.length === 0 && <p className="text-xs text-brand-muted">No customer groups configured.</p>}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Opening Stock" testid="menu-stock"><Input type="number" min={0} value={form.stock ?? 0} onChange={(e) => field("stock", e.target.value)} className="border-brand-line" /></Fld>
              <Fld label="Reorder Level" testid="menu-reorder"><Input type="number" min={0} value={form.reorder_level ?? 5} onChange={(e) => field("reorder_level", e.target.value)} className="border-brand-line" /></Fld>
            </div>
            <div className="border border-brand-line rounded-sm p-3 space-y-2" data-testid="menu-item-charges">
              <p className="text-sm font-semibold text-brand-text">Item-Level Charges</p>
              <p className="text-xs text-brand-muted">Applied per line. Flat = per unit (× qty); Percent = of line amount. Leave 0 to skip. Manage charge types under <b>Charge Types</b>.</p>
              {itemChargeTypes.length === 0 ? <p className="text-xs text-brand-muted">No item-level charge types defined.</p> :
                itemChargeTypes.map((t) => {
                  const e = (form.chargesMap || {})[t.id] || {};
                  return (
                    <div key={t.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`menu-charge-${t.id}`}>
                      <span className="col-span-6 text-sm">{t.name}</span>
                      <Select value={e.mode || "flat"} onValueChange={(v) => setForm((f) => ({ ...f, chargesMap: { ...(f.chargesMap || {}), [t.id]: { ...((f.chargesMap || {})[t.id] || {}), mode: v } } }))}>
                        <SelectTrigger className="border-brand-line h-9 col-span-3" data-testid={`menu-charge-mode-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="flat">Flat</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" min={0} value={e.value ?? ""} placeholder="0" onChange={(ev) => setForm((f) => ({ ...f, chargesMap: { ...(f.chargesMap || {}), [t.id]: { ...((f.chargesMap || {})[t.id] || {}), value: ev.target.value } } }))} className="border-brand-line h-9 col-span-3" data-testid={`menu-charge-value-${t.id}`} />
                    </div>
                  );
                })}
            </div>
            <p className="text-xs text-brand-muted bg-brand-bg border border-brand-line rounded-sm p-3" data-testid="menu-availability-note">
              Stock is managed from the <b>Inventory</b> screen. Availability is automatic: <b>Available</b> when stock &gt; 0, <b>Out of Stock</b> at 0. Deducted on order acceptance, restored on cancellation.
            </p>
          </div>
          <DialogFooter><Button onClick={saveMenu} data-testid="save-menu-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* INVENTORY (STOCK) DIALOG */}
      <Dialog open={!!invDlg} onOpenChange={(o) => !o && setInvDlg(null)}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="inventory-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Edit Stock — {form.name}</DialogTitle><DialogDescription className="sr-only">Update menu item stock level and reorder threshold</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Fld label="Stock" testid="inv-qty"><Input type="number" min={0} value={form.quantity ?? 0} onChange={(e) => field("quantity", e.target.value)} className="border-brand-line" /></Fld>
              <Fld label="Reorder Level" testid="inv-threshold"><Input type="number" min={0} value={form.low_stock_threshold ?? 0} onChange={(e) => field("low_stock_threshold", e.target.value)} className="border-brand-line" /></Fld>
            </div>
            <p className="text-xs text-brand-muted">Setting stock to 0 marks the item Out of Stock; any positive value marks it Available.</p>
          </div>
          <DialogFooter><Button onClick={saveInv} data-testid="save-inv-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!correctInv} onOpenChange={(o) => !o && setCorrectInv(null)}>
        <DialogContent className="bg-brand-bg max-w-lg max-h-[90vh] overflow-y-auto" data-testid="correct-invoice-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Correct Invoice {correctInv?.invoice_number}</DialogTitle><DialogDescription className="sr-only">Edit invoice line items under audit</DialogDescription></DialogHeader>
          <div className="space-y-2">
            {correctItems.map((it, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`correct-item-${i}`}>
                <Input value={it.name} onChange={(e) => setCI(i, "name", e.target.value)} className="border-brand-line col-span-5 h-9" />
                <Input type="number" value={it.price} onChange={(e) => setCI(i, "price", e.target.value)} className="border-brand-line col-span-3 h-9" placeholder="price" />
                <Input type="number" value={it.quantity} onChange={(e) => setCI(i, "quantity", e.target.value)} className="border-brand-line col-span-3 h-9" placeholder="qty" data-testid={`correct-qty-${i}`} />
                <button onClick={() => delCI(i)} className="text-brand-alert col-span-1"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <Fld label="Reason for correction"><Input value={correctReason} onChange={(e) => setCorrectReason(e.target.value)} className="border-brand-line" data-testid="correct-reason" /></Fld>
          </div>
          <DialogFooter><Button onClick={saveCorrect} data-testid="save-correct-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save Correction</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <InvoiceDialog invoice={viewInv} open={!!viewInv} onClose={() => setViewInv(null)} />

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent className="max-w-sm bg-brand-bg" data-testid="admin-payment-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Collect Payment</DialogTitle><DialogDescription className="sr-only">Record a payment against this invoice</DialogDescription></DialogHeader>
          {payFor && <div className="space-y-3">
            <p className="text-sm text-brand-muted">{payFor.invoice_number} · {payFor.member_name} · Balance {money(payFor.balance_due, sym)}</p>
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Payment Method</p>
              <div className="grid grid-cols-2 gap-2">
                {[{ k: "cash", l: "Cash" }, { k: "online", l: "Online" }].map((p) => (
                  <button key={p.k} onClick={() => setPayMode(p.k)} data-testid={`admin-paymode-${p.k}`}
                    className={`text-sm px-3 py-2 rounded-sm border transition-colors ${payMode === p.k ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line"}`}>{p.l}</button>
                ))}
              </div>
            </div>
            <Input type="number" value={payAmt} onChange={(e) => setPayAmt(e.target.value)} data-testid="admin-payment-amount" className="border-brand-line" placeholder="Amount" />
            <div>
              <p className="text-xs uppercase tracking-wider text-brand-muted mb-1">Remarks (optional)</p>
              <Input value={payRemarks} onChange={(e) => setPayRemarks(e.target.value)} data-testid="admin-payment-remarks" className="border-brand-line" placeholder="Txn ref, receipt no, notes..." />
            </div>
          </div>}
          <DialogFooter><Button onClick={collectPayment} data-testid="admin-confirm-payment" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Record Payment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <StockAdjustDialog item={adjItem} open={!!adjItem} onClose={() => setAdjItem(null)} onDone={loadAll} />

      {/* CUSTOMER GROUP DIALOG */}
      <Dialog open={!!groupDlg} onOpenChange={(o) => !o && setGroupDlg(null)}>
        <DialogContent className="bg-brand-bg max-w-lg max-h-[90vh] overflow-y-auto" data-testid="group-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">{groupDlg === "new" ? "Add Customer Group" : "Edit Customer Group"}</DialogTitle><DialogDescription className="sr-only">Manage customer group and its charges</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Fld label="Group Name" testid="group-name"><Input value={groupForm.name || ""} onChange={(e) => setGroupForm((f) => ({ ...f, name: e.target.value }))} className="border-brand-line" placeholder="e.g. Corporate" /></Fld>
            <Fld label="Group Discount %" testid="group-discount"><Input type="number" min={0} max={100} value={groupForm.discount_percent ?? ""} placeholder="0" onChange={(e) => setGroupForm((f) => ({ ...f, discount_percent: e.target.value }))} className="border-brand-line" /></Fld>
            <div className="border border-brand-line rounded-sm p-3 space-y-2" data-testid="group-charges">
              <p className="text-sm font-semibold text-brand-text">Group-Level Charges</p>
              <p className="text-xs text-brand-muted">Applied to the whole order for this group. Flat = once per order; Percent = of order subtotal. Leave 0 to skip.</p>
              {groupChargeTypes.length === 0 ? <p className="text-xs text-brand-muted">No group-level charge types defined.</p> :
                groupChargeTypes.map((t) => {
                  const e = (groupForm.chargesMap || {})[t.id] || {};
                  return (
                    <div key={t.id} className="grid grid-cols-12 gap-2 items-center" data-testid={`group-charge-${t.id}`}>
                      <span className="col-span-6 text-sm">{t.name}</span>
                      <Select value={e.mode || "flat"} onValueChange={(v) => setGroupCharge(t.id, "mode", v)}>
                        <SelectTrigger className="border-brand-line h-9 col-span-3" data-testid={`group-charge-mode-${t.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="flat">Flat</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
                      </Select>
                      <Input type="number" min={0} value={e.value ?? ""} placeholder="0" onChange={(ev) => setGroupCharge(t.id, "value", ev.target.value)} className="border-brand-line h-9 col-span-3" data-testid={`group-charge-value-${t.id}`} />
                    </div>
                  );
                })}
            </div>
            <div className="flex items-center gap-2"><Switch checked={groupForm.active !== false} onCheckedChange={(v) => setGroupForm((f) => ({ ...f, active: v }))} data-testid="group-active" /><span className="text-sm">Active</span></div>
            <div className="border border-brand-line rounded-sm p-3 space-y-2" data-testid="group-payment-modes">
              <p className="text-sm font-semibold text-brand-text">Payment Methods</p>
              <p className="text-xs text-brand-muted">Choose which methods members in this group can use. At least one must stay on. (Pay Now also needs Razorpay keys configured.)</p>
              <div className="flex items-center gap-2"><Switch checked={(groupForm.payment_modes || {}).cod !== false} onCheckedChange={(v) => setGroupMode("cod", v)} data-testid="group-mode-cod" /><span className="text-sm">Cash on Delivery</span></div>
              <div className="flex items-center gap-2"><Switch checked={(groupForm.payment_modes || {}).credit !== false} onCheckedChange={(v) => setGroupMode("credit", v)} data-testid="group-mode-credit" /><span className="text-sm">Credit</span></div>
              <div className="flex items-center gap-2"><Switch checked={!!(groupForm.payment_modes || {}).pay_now} onCheckedChange={(v) => setGroupMode("pay_now", v)} data-testid="group-mode-pay_now" /><span className="text-sm">Pay Now (Online)</span></div>
            </div>
          </div>
          <DialogFooter><Button onClick={saveGroup} data-testid="save-group-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHARGE TYPE DIALOG */}
      <Dialog open={!!chargeDlg} onOpenChange={(o) => !o && setChargeDlg(null)}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="charge-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">{chargeDlg === "new" ? "Add Charge Type" : "Edit Charge Type"}</DialogTitle><DialogDescription className="sr-only">Manage charge type definitions</DialogDescription></DialogHeader>
          <div className="space-y-3">
            <Fld label="Charge Name" testid="charge-name"><Input value={chargeForm.name || ""} onChange={(e) => setChargeForm((f) => ({ ...f, name: e.target.value }))} className="border-brand-line" placeholder="e.g. Item Corkage Charge" /></Fld>
            <Fld label="Scope" testid="charge-scope">
              <Select value={chargeForm.scope || "item"} onValueChange={(v) => setChargeForm((f) => ({ ...f, scope: v }))}>
                <SelectTrigger className="border-brand-line" data-testid="charge-scope-trigger"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="item">Item-level (assign on menu items)</SelectItem>
                  <SelectItem value="group">Group-level (assign on customer groups)</SelectItem>
                </SelectContent>
              </Select>
            </Fld>
            <div className="flex items-center gap-2"><Switch checked={chargeForm.active !== false} onCheckedChange={(v) => setChargeForm((f) => ({ ...f, active: v }))} data-testid="charge-active" /><span className="text-sm">Active</span></div>
          </div>
          <DialogFooter><Button onClick={saveCharge} data-testid="save-charge-button" className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm w-full">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <OrderEditDialog order={editOrder} menu={menu} open={!!editOrder} onClose={() => setEditOrder(null)} onDone={loadAll} />

      {/* MASTER DATA RESET */}
      <Dialog open={resetDlg} onOpenChange={(o) => !o && setResetDlg(false)}>
        <DialogContent className="bg-brand-bg max-w-md" data-testid="master-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl tracking-tight text-left text-brand-alert">Reset Master Data</DialogTitle>
            <DialogDescription className="text-left">Do you really want to reset data? All transaction data will be deleted and cannot be restored later.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="text-sm text-brand-muted bg-white border border-brand-line rounded-sm p-3">
              <p className="font-semibold text-brand-text mb-1">This will delete:</p>
              Orders, Invoices, Expenses, Stock Adjustments, Day-End &amp; Year-End records.<br />
              <span className="font-semibold text-brand-text">And reset:</span> member balances → 0, menu stock → 0, numbering → 1.<br />
              <span className="font-semibold text-brand-text">Kept:</span> users, menu items, categories, club settings, audit log.
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold">Type <b>RESET</b> to confirm</Label>
              <Input value={resetText} onChange={(e) => setResetText(e.target.value)} placeholder="RESET" data-testid="master-reset-input" className="border-brand-line" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={doReset} disabled={resetting || resetText.trim() !== "RESET"} data-testid="confirm-master-reset" className="bg-brand-alert hover:bg-brand-alert/90 text-white rounded-sm w-full">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Permanently Reset Data"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

function Section({ title, onAdd, addLabel, addTestId, children }) {
  return (
    <>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-2">
        <h1 className="font-serif text-3xl sm:text-4xl tracking-tighter text-brand-text">{title}</h1>
        <Button onClick={onAdd} data-testid={addTestId} className="bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm gap-2"><Plus className="w-4 h-4" /> {addLabel}</Button>
      </div>
      <div className="bg-white border border-brand-line rounded-md overflow-hidden overflow-x-auto">{children}</div>
    </>
  );
}

function Fld({ label, testid, children }) {
  return (
    <div className="space-y-1.5" data-testid={testid}>
      <Label className="text-sm font-semibold text-brand-text">{label}</Label>
      {children}
    </div>
  );
}
